import { DropdownRenderV2 } from "#src/commonV2/antdHelpers/DropdownRender";
import { BadgeV2 } from "#src/commonV2/badges/Badge";
import { BadgeSeeMoreV2 } from "#src/commonV2/badges/BadgeSeeMore";
import { LoaderV2 } from "#src/commonV2/LoadingState";
import { TableV2 } from "#src/commonV2/Table";
import { FilterDropdownV2 } from "#src/commonV2/Table/Filter/FilterDropdown";
import { useFilter } from "#src/commonV2/Table/Filter/useFilter";
import { SearchboxV2 } from "#src/commonV2/Table/Searchbox";
import { TableActionLabelV2 } from "#src/commonV2/Table/TableActionLabel";
import {
  fileSensitivityItems,
  fileTypeItems,
} from "#src/components/endpoint/devices/details/drive/filterItems";
import { EndpointDriveLabelingModal } from "#src/components/endpoint/devices/details/drive/LabelingModal";
import { EndpointDeviceManageSnapshotModal } from "#src/components/endpoint/devices/details/drive/ManageSnapshotModal";
import { IFileItem } from "#src/components/endpoint/devices/details/drive/types";
import { SensitivityLabel } from "#src/components/endpoint/dlp/dataLabeling/Sensitivity";
import { getIconFromFileExtension } from "#src/components/endpoint/dlp/dataLabeling/utils";
import useFetchNoPaging from "#src/hooks/useFetchNoPaging";
import { endpointService } from "#src/services/endpoint";
import { IEndpointDevice } from "#src/services/endpoint/devices/types";
import dlpService from "#src/services/endpoint/dlp";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { byteToMegabyte } from "#src/utils/common";
import { Dropdown, Tooltip } from "@lockerpm/design";
import {
  ChevronDown,
  ChevronRight,
  DotsVertical,
  Folder,
  HardDrive,
  Tag01,
  Transform,
} from "@untitled-ui/icons-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";

export enum EndpointDeviceDriveSearchParamKey {
  FILE_TYPE = "type",
  SENSITIVITY = "sensitivity",
  CONTENT_TAG = "content",
}

export const HostDrive = () => {
  const { t, i18n } = useTranslation("endpoint", {
    keyPrefix: "devices.drive",
  });

  const { hostDetails, workspaceId, hostId } = useOutletContext<{
    hostDetails: IEndpointDevice;
    workspaceId: string;
    hostId: string;
  }>();

  if (!hostDetails) return null;

  // --------------- DATA ---------------

  const [rootPath, setRootPath] = useState<string[]>(["C:"]);

  // Search/sort
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("");

  const [isLoadingDrivePath, setLoadingDrivePath] = useState<boolean>(false);

  const [openManageSnapshotModal, setOpenManageSnapshotModal] =
    useState<IFileItem | null>(null);
  const [openLabelingModal, setOpenLabelingModal] = useState<IFileItem | null>(
    null
  );

  // --------------- FILTERS ---------------

  const fetchTagParams = useMemo(() => [workspaceId], [workspaceId]);

  const { list: contentTagList } = useFetchNoPaging(
    dlpService.listDataLabelContent,
    fetchTagParams
  );

  const { list: sensitivityTagList } = useFetchNoPaging(
    dlpService.listDataLabelSensitivity,
    fetchTagParams
  );

  const { value: fileType, renderProps: fileTypeFilterRenderProps } = useFilter(
    {
      label: t("table.filter.type.label"),
      selectionList: fileTypeItems,
      options: {
        searchParamKey: EndpointDeviceDriveSearchParamKey.FILE_TYPE,
      },
    }
  );

  const {
    value: fileSensitivity,
    renderProps: fileSensitivityFilterRenderProps,
  } = useFilter({
    label: t("table.filter.sensitivity.label"),
    selectionList:
      sensitivityTagList === null
        ? []
        : [
            ...sensitivityTagList.map((tag) => ({
              key: tag.id,
              value: tag.id,
              getLabel: () =>
                i18n.language === "vi" ? tag.name.vi : tag.name.en,
            })),
            // The not_applicable item, This doesn't exist in backend.
            fileSensitivityItems[fileSensitivityItems.length - 1],
          ],
    options: {
      searchParamKey: EndpointDeviceDriveSearchParamKey.SENSITIVITY,
    },
  });

  const {
    value: fileContentTag,
    renderProps: fileContentTagFilterRenderProps,
  } = useFilter({
    label: t("table.filter.content.label"),
    selectionList:
      contentTagList === null
        ? []
        : contentTagList.map((tag) => ({
            key: tag.id.toString(),
            value: tag.id.toString(),
            getLabel: () => tag.name,
          })),
    options: {
      searchParamKey: EndpointDeviceDriveSearchParamKey.CONTENT_TAG,
      itemLabel: "badge",
    },
    multiple: true,
  });

  // --------------- PARAMETERS ---------------

  const fetchParams = useMemo<
    Parameters<typeof endpointService.listDeviceDrives>
  >(
    () => [
      workspaceId,
      hostId,
      {
        cur_ext: fileType,
        sensitivity_level_id: fileSensitivity,
        content_type_id: fileContentTag,
        // currently we fetch all data and calculate what to show by our own, so re-fetch when path is changing is not necessary
        cur_path: undefined,
        cur_name: searchKeyword,
      },
    ],
    [
      workspaceId,
      hostId,
      fileType,
      fileSensitivity,
      fileContentTag,
      searchKeyword,
    ]
  );

  // --------------- HOOKS ---------------

  const {
    list: fileList,
    isLoading,
    updateData: updateFileList,
  } = useFetchNoPaging(endpointService.listDeviceDrives, fetchParams);

  // --------------- METHODS ---------------

  // --------------- COMPUTED ---------------

  const getActionList = (record: IFileItem) => {
    if (record.type === "Folder") return [];

    const actionList: {
      key: string;
      label: ReactNode;
      action: () => void;
    }[] = [
      {
        key: "snapshot",
        label: (
          <TableActionLabelV2
            icon={<Transform className="h-5 w-5" />}
            text={t("table.action.restorePreviousVersion")}
          />
        ),
        action: () => {
          setOpenManageSnapshotModal(record);
        },
      },
      {
        key: "label",
        label: (
          <TableActionLabelV2
            icon={<Tag01 className="h-5 w-5" />}
            text={t("table.action.labelDocument")}
          />
        ),
        action: () => {
          setOpenLabelingModal(record);
        },
      },
    ];

    return actionList;
  };

  /** Parse files into folders in current chosen path. */
  const foldermized = useMemo<IFileItem[]>(() => {
    if (fileList === null) return [];

    const folders: IFileItem[] = [];

    const filesOfCurrentFolder: IFileItem[] = fileList
      .filter((drive) => {
        // If this file's path matches the path of current folder, return true and add the item to the table.
        if ([...rootPath, drive.cur_name].join("/") === drive.cur_path) {
          return true;
        }

        // If root path is not matched with file's path, eliminate this file from display.
        if (!drive.cur_path.includes(rootPath.join("/"))) {
          return false;
        }

        // Parse item's path to find its folder name.
        const pathSplitted = drive.cur_path
          .split("/")
          .filter((item) => item.length > 0);

        // If file's path is shorter than current folder path, eliminate this file from display.
        if (rootPath.length >= pathSplitted.length) {
          return false;
        }

        const folderName = pathSplitted[rootPath.length];

        if (folderName.includes(".")) {
          console.log(
            'Broken folder path. No folder should have "." in its name. There might be some mismatched file name & path.'
          );
          return false;
        }

        // Ignore folders that already exist.
        if (!folders.map((f) => f.name).includes(folderName)) {
          folders.push({
            id: drive.id,
            name: folderName,
            type: "Folder",
            sensitivity: [],
            content: [],
            snapshotCount: -1,
            size: "---",
          });
        }

        return false;
      })
      .map((drive): IFileItem => {
        return {
          id: drive.id,
          name: drive.cur_name,
          type: drive.cur_ext,
          sensitivity:
            drive.sensitivity_level === null
              ? []
              : drive.sensitivity_level.map((item) => ({
                  id: item.id,
                  name: item.name,
                })),
          content: drive.content_types,
          snapshotCount: drive.snapshot_number,
          size: byteToMegabyte(drive.total_size).toFixed(1).toString(),
        };
      });

    return [...folders, ...filesOfCurrentFolder];
  }, [fileList, rootPath]);

  // --------------- EFFECTS ---------------

  useEffect(() => {
    setLoadingDrivePath(true);
    endpointService
      .retrieveDeviceDrivePath(workspaceId, hostId)
      .then((response) => {
        const pathSegment = response.drive_path
          .split("/")
          .filter((item) => item.length > 0);

        setRootPath(pathSegment);
        setLoadingDrivePath(false);
      })
      .catch(apiErrorHandler);
  }, [workspaceId, hostId]);

  // --------------- RENDER ---------------

  return (
    <>
      <div className="flex flex-col gap-4 flex-1">
        <div className="bg-gray-v2-50 border border-gray-v2-200 flex gap-0.5 w-fit rounded-lg text-gray-v2-500 text-sm font-semibold">
          {isLoadingDrivePath || rootPath.length === 0 ? (
            <div className="px-3 py-2.5 flex items-center gap-2 text-gray-v2-900 bg-white border border-gray-v2-300 rounded-lg shadow-xs">
              <HardDrive className="h-5 w-5 min-w-5" />
              {rootPath.length === 0 ? null : (
                <LoaderV2 baseSize={1.25} particleCount={24} />
              )}
            </div>
          ) : null}
          {rootPath.map((rootChop, chopId) =>
            chopId < rootPath.length - 1 ? (
              <div
                key={chopId}
                className="px-3 py-2.5 flex items-center gap-2 rounded-lg"
              >
                <button
                  className="flex items-center gap-2"
                  onClick={() => {
                    setRootPath((prev) => prev.slice(0, chopId + 1));
                  }}
                >
                  {chopId === 0 ? (
                    <HardDrive className="h-5 w-5 min-w-5" />
                  ) : (
                    <Folder className="h-5 w-5 min-w-5" />
                  )}
                  {rootChop}
                </button>
                <ChevronRight className="h-4 w-4 min-w-4 text-gray-v2-300" />
              </div>
            ) : (
              <button
                key={chopId}
                className="px-3 py-2.5 flex items-center gap-2 text-gray-v2-900 bg-white border border-gray-v2-300 rounded-lg shadow-xs"
              >
                {chopId === 0 ? (
                  <HardDrive className="h-5 w-5 min-w-5" />
                ) : (
                  <Folder className="h-5 w-5 min-w-5" />
                )}
                <span className="font-medium">{rootChop}</span>
                <ChevronDown className="h-4 w-4 min-w-4 text-gray-v2" />
              </button>
            )
          )}
        </div>
        <div className="flex justify-between items-start gap-2">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <FilterDropdownV2 {...fileTypeFilterRenderProps} />
            <FilterDropdownV2 {...fileSensitivityFilterRenderProps} />
            <FilterDropdownV2 {...fileContentTagFilterRenderProps} />
          </div>
          {/* Filters end */}

          {/* Searchbox */}
          <SearchboxV2
            value={searchKeyword}
            onChange={(value) => {
              setSearchKeyword(value);
            }}
            placeholder={t("table.filter.searchbox.placeholder")}
          />
          {/* Searchbox end */}
        </div>

        {/* Table */}
        <TableV2<IFileItem>
          data={foldermized}
          getRowKey={(item) => item.id.toString()}
          isLoading={isLoading}
          columns={[
            {
              title: t("table.header.name"),
              render: (item) => (
                <Tooltip title={item.name}>
                  <div className="flex items-center gap-3 font-medium text-gray-v2-900">
                    {item.type === "Folder" ? (
                      item.name.includes(":") ? (
                        <HardDrive className="text-gray-v2 h-8 w-8 min-w-8" />
                      ) : (
                        <Folder className="text-gray-v2 h-8 w-8 min-w-8" />
                      )
                    ) : (
                      getIconFromFileExtension(item.type, 2)
                    )}
                    {item.name}
                  </div>
                </Tooltip>
              ),
              gridTemplateValue: "1fr",
            },
            {
              title: t("table.header.type"),
              render: (item) => item.type,
              gridTemplateValue: "1fr",
            },
            {
              title: t("table.header.sensitivity"),
              render: (item) =>
                item.sensitivity.length === 0
                  ? "---"
                  : item.sensitivity.map((s) => (
                      <SensitivityLabel key={s.id} sensitivity={s} />
                    )),
              gridTemplateValue: "1fr",
            },
            {
              title: t("table.header.content"),
              render: (item) =>
                item.content.length === 0 ? (
                  "---"
                ) : (
                  <>
                    {item.content.slice(0, 2).map((tag) => (
                      <BadgeV2 key={tag.id} name={tag.name} defaultIcon />
                    ))}
                    {item.content.length > 2 ? (
                      <BadgeSeeMoreV2
                        badges={item.content.slice(2).map((item) => (
                          <BadgeV2 key={item.id} name={item.name} defaultIcon />
                        ))}
                      />
                    ) : null}
                  </>
                ),
              gridTemplateValue: "2fr",
              overwriteWrapperProps: { className: "gap-1" },
            },
            {
              title: t("table.header.snapshots"),
              render: (item) =>
                item.snapshotCount < 0
                  ? "---"
                  : item.snapshotCount +
                    " " +
                    t("table.version", { count: item.snapshotCount }),
              gridTemplateValue: "1fr",
            },
            {
              title: t("table.header.snapshotTotalSize"),
              render: (item) => item.size,
              gridTemplateValue: "1fr",
            },
            {
              title: null,
              render: (item) => {
                const actionList = getActionList(item);

                if (actionList.length === 0) return null;
                return (
                  <Dropdown
                    menu={{
                      items: actionList.map((action) => ({
                        key: action.key,
                        label: action.label,
                      })),
                      onClick: ({ key, domEvent }) => {
                        domEvent.preventDefault();
                        domEvent.stopPropagation();
                        actionList
                          .find((action) => action.key === key)
                          ?.action();
                      },
                    }}
                    dropdownRender={(menu) => (
                      <DropdownRenderV2>{menu}</DropdownRenderV2>
                    )}
                    trigger={["click"]}
                  >
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <DotsVertical
                        className={`h-5 w-5 text-gray-v2-400 transition-colors duration-300`}
                      />
                    </button>
                  </Dropdown>
                );
              },
              overwriteWrapperProps: { className: "justify-center" },
              gridTemplateValue: "6rem",
            },
          ]}
          sort={{
            value: sortOrder,
            onChange: (value) => {
              setSortOrder(value);
            },
          }}
          options={{
            fitPageHeight: true,
            disableRowCursor: (item) => item.type !== "Folder",
          }}
          onClickRow={(item) => {
            if (item.type !== "Folder") return;
            setRootPath((prev) => [...prev, item.name]);
          }}
        />
        {/* Table end */}
      </div>
      <EndpointDeviceManageSnapshotModal
        workspaceId={workspaceId}
        deviceId={hostId}
        fileId={openManageSnapshotModal?.id}
        open={openManageSnapshotModal !== null}
        onClose={() => {
          setOpenManageSnapshotModal(null);
        }}
        selectedFileItem={openManageSnapshotModal}
      />
      <EndpointDriveLabelingModal
        workspaceId={workspaceId}
        deviceId={hostId}
        open={openLabelingModal !== null}
        onClose={() => {
          setOpenLabelingModal(null);
        }}
        onRefresh={updateFileList}
        contentTagList={contentTagList ?? []}
        selectedFileItem={openLabelingModal}
      />
    </>
  );
};
